import type { DBLocationOverview } from "@/lib/db-types";

export type IndoorFilter = "all" | "indoor" | "outdoor";

export interface LocationFilter {
  search: string;
  /** Type-id, eller null for alle. */
  typeId: string | null;
  /** Kommunenavn, eller null for alle. */
  municipality: string | null;
  indoor: IndoorFilter;
  showArchived: boolean;
  /** Bare lokasjoner med status Brukt på et prosjekt (VIB-2940). */
  usedOnProject: boolean;
}

export const EMPTY_FILTER: LocationFilter = {
  search: "",
  typeId: null,
  municipality: null,
  indoor: "all",
  showArchived: false,
  usedOnProject: false,
};

/** Søk i navn, adresse, kommune, bydel, type, beskrivelse og stikkord. */
export function matchesLocationSearch(l: DBLocationOverview, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return [l.name, l.address, l.municipality_name, l.district, l.type_label, l.description, ...l.tags].some((v) =>
    (v ?? "").toLowerCase().includes(q),
  );
}

/** «both» passer både inne og ute; uklassifiserte vises bare under «alle». */
function matchesIndoor(l: DBLocationOverview, indoor: IndoorFilter): boolean {
  if (indoor === "all") return true;
  return l.indoor === indoor || l.indoor === "both";
}

/**
 * Arkiv, søk, kommune og inne/ute først, så antall per type (til chipene), så
 * typen. Sortert på sist endret.
 */
export function filterLocations(rows: DBLocationOverview[], f: LocationFilter) {
  const scoped = rows.filter(
    (l) =>
      (f.showArchived ? l.archived_at !== null : l.archived_at === null) &&
      (f.municipality === null || l.municipality_name === f.municipality) &&
      matchesIndoor(l, f.indoor) &&
      (!f.usedOnProject || l.used_count > 0) &&
      matchesLocationSearch(l, f.search),
  );
  const countsByType = new Map<string, number>();
  for (const l of scoped) {
    if (l.type_id) countsByType.set(l.type_id, (countsByType.get(l.type_id) ?? 0) + 1);
  }
  const filtered = f.typeId === null ? scoped : scoped.filter((l) => l.type_id === f.typeId);
  const sorted = [...filtered].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  return { rows: sorted, countsByType, scopedTotal: scoped.length };
}

/** Kommunene som finnes i banken, sortert, til kommunefilteret. */
export function municipalitiesOf(rows: DBLocationOverview[]): string[] {
  const set = new Set<string>();
  for (const l of rows) if (l.municipality_name && l.archived_at === null) set.add(l.municipality_name);
  return [...set].sort((a, b) => a.localeCompare(b, "nb"));
}

/** «Oslo · Sagene · Studio», med det som finnes. */
export function locationSubtitle(l: Pick<DBLocationOverview, "municipality_name" | "district" | "type_label" | "address">): string {
  const parts = [l.municipality_name, l.district, l.type_label].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : (l.address ?? "");
}
